// Офлайн-очередь событий и синхронизация со снимком сервера.
//
// Каждое действие сначала пишется в outbox с уникальным id и только потом отправляется.
// Сервер идемпотентен по id, поэтому повторная отправка безопасна.
// Подтверждённые события остаются в outbox со статусом 'sent', пока не придёт снимок,
// загруженный после подтверждения: до этого они нужны, чтобы интерфейс учитывал их локально.

import { isUnauthorized, NetworkError, type Api } from './api.ts';
import type { Store } from './store.ts';
import type { LearningEvent, OutboxRecord, Settings, Snapshot, Unit } from './types.ts';

const BATCH = 100;
const KEEP_REJECTED_MS = 7 * 24 * 3600 * 1000;

export type SyncStatus = 'idle' | 'syncing' | 'offline' | 'auth';

export class Sync {
  private flushing: Promise<void> | null = null;
  private seq = 0;
  status: SyncStatus = 'idle';
  snapshot: Snapshot | null = null;
  outbox: OutboxRecord[] = [];
  onChange: () => void = () => {};
  onAuthRequired: () => void = () => {};

  private store: Store;
  private api: Api;
  private now: () => number;

  constructor(store: Store, api: Api, now: () => number = Date.now) {
    this.store = store;
    this.api = api;
    this.now = now;
  }

  async load(): Promise<void> {
    this.snapshot = (await this.store.kvGet<Snapshot>('snapshot')) ?? null;
    this.outbox = await this.store.outboxAll();
  }

  /** События, которые сервер ещё не отразил в текущем снимке. */
  localEvents(): LearningEvent[] {
    return this.outbox.filter((r) => r.state !== 'rejected').map((r) => r.event);
  }

  pendingCount(): number {
    return this.outbox.filter((r) => r.state === 'pending').length;
  }

  /** Сохраняет событие на устройстве и запускает отправку. */
  async enqueue(event: LearningEvent): Promise<void> {
    if (this.outbox.some((r) => r.event.id === event.id)) return;
    const rec: OutboxRecord = { event, seq: this.now() * 1000 + (this.seq++ % 1000), state: 'pending' };
    await this.store.outboxPut(rec);
    this.outbox.push(rec);
    this.onChange();
    void this.flush();
  }

  /** Отправляет неотправленные события; при успехе обновляет снимок. Повторный вызов ждёт текущий. */
  flush(): Promise<void> {
    if (!this.flushing) {
      this.flushing = this.doFlush().finally(() => {
        this.flushing = null;
      });
    }
    return this.flushing;
  }

  private async doFlush(): Promise<void> {
    this.setStatus('syncing');
    try {
      let sentAny = false;
      for (;;) {
        const batch = this.outbox.filter((r) => r.state === 'pending').slice(0, BATCH);
        if (!batch.length) break;
        const results = await this.api.sendEvents(batch.map((r) => r.event));
        const byId = new Map(results.map((r) => [r.id, r]));
        for (const rec of batch) {
          const res = byId.get(rec.event.id);
          if (!res) continue;
          if (res.status === 'rejected') {
            rec.state = 'rejected';
            rec.reason = res.reason;
            console.warn('event rejected', rec.event, res.reason);
          } else {
            rec.state = 'sent';
          }
          rec.confirmedAt = this.now();
          await this.store.outboxPut(rec);
        }
        sentAny = true;
        if (batch.every((r) => r.state === 'pending')) break; // сервер не ответил ни по одному — не зацикливаемся
      }
      if (sentAny || !this.snapshot) await this.refreshSnapshot();
      this.setStatus('idle');
    } catch (e) {
      this.handleError(e);
    }
  }

  /** Загружает снимок и убирает из outbox события, которые он уже учитывает. */
  async refreshSnapshot(): Promise<void> {
    const startedAt = this.now();
    const snap = await this.api.snapshot();
    await this.store.kvPut('snapshot', snap);
    this.snapshot = snap;
    const drop = this.outbox.filter(
      (r) =>
        (r.state === 'sent' && (r.confirmedAt ?? Infinity) <= startedAt) ||
        (r.state === 'rejected' && startedAt - (r.confirmedAt ?? 0) > KEEP_REJECTED_MS),
    );
    await this.store.outboxDelete(drop.map((r) => r.event.id));
    const dropIds = new Set(drop.map((r) => r.event.id));
    this.outbox = this.outbox.filter((r) => !dropIds.has(r.event.id));
    this.onChange();
  }

  /** Полная синхронизация: отправить, затем обновить снимок (даже если отправлять нечего). */
  async syncNow(): Promise<void> {
    await this.flush();
    if (this.status === 'idle') {
      try {
        await this.refreshSnapshot();
      } catch (e) {
        this.handleError(e);
      }
    }
  }

  /** Подменяет единицу в локальном снимке ответом сервера (после добавления или правки онлайн). */
  async upsertUnit(u: Unit): Promise<void> {
    if (!this.snapshot) return;
    const units = this.snapshot.units.filter((x) => x.id !== u.id);
    this.snapshot = { ...this.snapshot, units: [...units, u] };
    await this.store.kvPut('snapshot', this.snapshot);
    this.onChange();
  }

  /** Убрать удалённую на сервере единицу из локального снимка. */
  async removeUnit(id: string): Promise<void> {
    if (!this.snapshot) return;
    this.snapshot = { ...this.snapshot, units: this.snapshot.units.filter((x) => x.id !== id) };
    await this.store.kvPut('snapshot', this.snapshot);
    this.onChange();
  }

  async setSettings(s: Settings): Promise<void> {
    if (!this.snapshot) return;
    this.snapshot = { ...this.snapshot, settings: s };
    await this.store.kvPut('snapshot', this.snapshot);
    this.onChange();
  }

  /** Очищает локальные данные при выходе (неотправленные события сохраняются, если они есть). */
  async resetAfterLogout(): Promise<void> {
    await this.store.kvDelete('snapshot');
    this.snapshot = null;
    this.onChange();
  }

  private handleError(e: unknown) {
    if (isUnauthorized(e)) {
      // Очередь сохраняется: после повторного входа события уйдут.
      this.setStatus('auth');
      this.onAuthRequired();
    } else if (e instanceof NetworkError) {
      this.setStatus('offline');
    } else {
      console.error(e);
      this.setStatus('offline');
    }
  }

  private setStatus(s: SyncStatus) {
    if (this.status !== s) {
      this.status = s;
      this.onChange();
    }
  }
}

/** Отправка после старта, при появлении сети и раз в минуту, пока есть что отправлять. */
export function startAutoSync(sync: Sync): () => void {
  const onOnline = () => void sync.syncNow();
  window.addEventListener('online', onOnline);
  const timer = setInterval(() => {
    if (sync.pendingCount() > 0 && sync.status !== 'auth') void sync.flush();
  }, 60_000);
  void sync.syncNow();
  return () => {
    window.removeEventListener('online', onOnline);
    clearInterval(timer);
  };
}
